import {FinancialDisclosure,continueCheckout,type FinancialAcceptance} from './FinancialDisclosure';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, ApiRequestError } from '../../lib/api';
import { BOOLEAN_CHOICES, type ActivePageResponse, type AppointmentResponse, type CreateAppointmentRequest, type CustomerDetails, type PublicQuestion, type PublicService, type PublicSession, type Slot } from '../types';
import { locationsFor, providersFor } from '../lib/bookingPage';
import { browserTimeZone, dateInZone, formatDateTime, formatDuration, formatMoney } from '../lib/format';
import { buildIcs, icsDataUrl } from '../lib/ics';
import { StepIndicator, type StepDef } from './StepIndicator';
import { DateTimePicker } from './DateTimePicker';
import { WaitlistForm } from './WaitlistForm';
import { ClassPicker } from './ClassPicker';
import { AppointmentSummary } from './AppointmentSummary';
import { ContactList, hasContact } from './BusinessHeader';
import { hasPaymentInfo, HowToPay, PaidBadge, PaymentMethods } from './Payment';
import { CalendarIcon, CheckIcon, LinkIcon } from './Icons';
import { useI18n } from '../lib/i18n';
import { SmsConsent } from './SmsConsent';

type StepId = 'service' | 'location' | 'preference' | 'provider' | 'time' | 'details';

interface Props {
  slug: string;
  data: ActivePageResponse;
}

const EMPTY_DETAILS: CustomerDetails = { name: '', email: '', phone: '', notes: '' };

export function BookingFlow({ slug, data }: Props) {
  const { t, locale } = useI18n();
  const { page } = data;
  const customerZone = useMemo(browserTimeZone, []);

  const [service, setService] = useState<PublicService | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  /** null = "any available" (or the only provider). */
  const [providerId, setProviderId] = useState<string | null>(null);
  const [anyProvider, setAnyProvider] = useState(false);
  /**
   * Provider preference (0051): what the customer asked for about WHO
   * provides the service, where the business offers the choice. A wish
   * about the provider, never a fact about the customer. Null until
   * asked; 'no_preference' once they said so.
   */
  const [preference, setPreference] = useState<string | null>(null);
  const preferenceOptions = page.provider_preference?.options ?? null;
  const asksPreference = preferenceOptions !== null && preferenceOptions.length > 1;
  /** A narrowing preference: the server picks among eligible people, and nobody is named. */
  const narrowed = preference !== null && preference !== 'no_preference';
  const [slot, setSlot] = useState<Slot | null>(null);
  /**
   * The day the last chosen time was on. The picker is unmounted while the
   * customer fills in their details, so this is what sends them back to the
   * day they were looking at — rather than to an empty calendar — when they
   * change their time, or when the one they wanted goes to somebody else.
   */
  const [lastDate, setLastDate] = useState<string | null>(null);
  const [details, setDetails] = useState<CustomerDetails>(EMPTY_DETAILS);
  const [step, setStep] = useState<StepId>('service');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** A 409 that isn't about the slot (e.g. the business can't take this booking online): shown as-is, with how to reach them. */
  const [refusal, setRefusal] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [result, setResult] = useState<AppointmentResponse | null>(null);
  /** The day a customer asked to be waitlisted for, or null when they haven't. */
  const [waitlistDate, setWaitlistDate] = useState<string | null>(null);
  /**
   * Classes for the chosen service. Loaded once the service is known,
   * because a service with classes is booked by picking one, not by
   * picking a time on a calendar.
   */
  const [sessions, setSessions] = useState<PublicSession[] | null>(null);
  /**
   * True while the chosen service runs classes at all — including when
   * every one of them is full. Full classes are not on offer, so the list
   * can be empty for a service that is nothing but classes, and falling
   * back to a calendar there would sell one-to-one times the business
   * does not have.
   */
  const [hasClasses, setHasClasses] = useState(false);
  /** Bumped when a class fills up under a customer: the counts are stale. */
  const [classToken, setClassToken] = useState(0);
  useEffect(() => {
    if (!service) { setSessions(null); setHasClasses(false); return; }
    let live = true;
    api<{ sessions: PublicSession[]; has_classes?: boolean }>(`/public/${slug}/sessions?service_id=${service.id}`)
      .then((r) => { if (live) { setSessions(r.sessions); setHasClasses(r.has_classes ?? r.sessions.length > 0); } })
      // A booking page that cannot list classes still books ordinary
      // appointments; it must not fail closed over them.
      .catch(() => { if (live) { setSessions([]); setHasClasses(false); } });
    return () => { live = false; };
  }, [slug, service, classToken]);
  const isClass = hasClasses;
  /** What the customer has typed against each of the service's questions. */
  const [answers, setAnswers] = useState<Record<string, string>>({});
  /** Per-question messages from a rejected booking, keyed by question id. */
  const [answerErrors, setAnswerErrors] = useState<Record<string, string>>({});

  const questions = service?.questions ?? [];

  const locations = service ? locationsFor(data, service) : [];
  const needsLocation = locations.length > 1;
  const location = needsLocation ? locations.find((l) => l.id === locationId) ?? null : locations[0] ?? data.locations[0] ?? null;
  const providers = service ? providersFor(data, service, location?.id) : [];
  // Eligibility is not published, so a customer who stated a preference
  // is not shown the list of names: the server assigns an eligible person.
  const needsProvider = page.allow_provider_choice && providers.length > 1 && !narrowed;
  const locationZone = location?.timezone ?? data.organization.timezone;

  const steps: StepDef[] = [
    { id: 'service', label: t('booking.step_service') },
    ...(needsLocation ? [{ id: 'location', label: t('booking.step_location') }] : []),
    ...(asksPreference ? [{ id: 'preference', label: t('booking.step_preference') }] : []),
    ...(needsProvider ? [{ id: 'provider', label: t('booking.step_provider') }] : []),
    { id: 'time', label: t('booking.step_date_time') },
    { id: 'details', label: t('booking.step_details') },
  ];
  const stepIndex = steps.findIndex((s) => s.id === step);

  const [financial,setFinancial]=useState<FinancialAcceptance>({quote:null,accepted:false,ready:false,priceKind:'card'});
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    const s = steps[stepIndex];
    if (s) setAnnouncement(`Step ${stepIndex + 1} of ${steps.length}: ${s.label}`);
  }, [step, stepIndex, steps.length, steps[stepIndex]?.label]);

  function go(next: StepId) {
    setError(null);
    setRefusal(null);
    setStep(next);
    document.getElementById('booking')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  function chooseSlot(s: Slot | null) {
    // A null means the customer moved to another day; the day itself is
    // only known from a time, so the last one stands until they pick again.
    if (s) setLastDate(dateInZone(locationZone, s.starts_at));
    setSlot(s);
  }

  function chooseService(s: PublicService) {
    setService(s);
    // The questions belong to the service, so what was typed for another one
    // means nothing here.
    setAnswers({});
    setAnswerErrors({});
    setLocationId(null);
    setProviderId(null);
    setAnyProvider(false);
    setSlot(null);
    const locs = locationsFor(data, s);
    if (locs.length > 1) return go('location');
    if (asksPreference) return go('preference');
    const provs = providersFor(data, s, locs[0]?.id);
    go(page.allow_provider_choice && provs.length > 1 && !narrowed ? 'provider' : 'time');
  }

  function chooseLocation(id: string) {
    setLocationId(id);
    setProviderId(null);
    setSlot(null);
    if (asksPreference) return go('preference');
    const provs = service ? providersFor(data, service, id) : [];
    go(page.allow_provider_choice && provs.length > 1 && !narrowed ? 'provider' : 'time');
  }

  function choosePreference(value: string) {
    setPreference(value);
    setProviderId(null);
    setAnyProvider(false);
    setSlot(null);
    const provs = service ? providersFor(data, service, location?.id) : [];
    go(page.allow_provider_choice && provs.length > 1 && value === 'no_preference' ? 'provider' : 'time');
  }

  /** Offered by the time step when the preference is why nothing is available. The customer chooses; nothing is dropped for them. */
  function clearPreference() {
    setPreference('no_preference');
    setSlot(null);
    setNotice(null);
  }

  function chooseProvider(id: string | null) {
    setProviderId(id);
    setAnyProvider(id === null);
    setSlot(null);
    go('time');
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!service || !slot) return;
    setSubmitting(true);
    setError(null);
    setRefusal(null);
    setAnswerErrors({});
    const body: CreateAppointmentRequest = {
      financial_policy_token:financial.quote?.token,accept_financial_policy:financial.accepted,
      // The PATH the customer chose (0045); the server prices it.
      price_kind:financial.priceKind,
      service_id: service.id,
      provider_id: slot.provider_id,
      provider_selection: anyProvider || (!page.allow_provider_choice && providers.length > 1 && !narrowed) ? 'next_available' : 'specific',
      ...(asksPreference && preference ? { provider_preference: preference } : {}),
      starts_at: slot.starts_at,
      customer: {
        sms_opt_in: details.sms_opt_in === true,
        name: details.name.trim(),
        email: details.email.trim(),
        ...(details.phone.trim() ? { phone: details.phone.trim() } : {}),
        timezone: customerZone,
        // The language they booked in, so their confirmation and
        // reminders arrive in the same language as this page.
        locale,
      },
      ...(details.notes.trim() ? { notes: details.notes.trim() } : {}),
      ...(questions.length > 0
        ? { answers: questions.map((q) => ({ question_id: q.id, answer: (answers[q.id] ?? '').trim() })) }
        : {}),
    };
    try {
      const res = await api<AppointmentResponse>(`/public/${encodeURIComponent(slug)}/appointments`, { method: 'POST', json: body });
      if(await continueCheckout(slug,res))return;
      setResult(res);
      setAnnouncement(t('booking.booked_announcement'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 409 && err.code === 'slot_unavailable') {
        // Someone took that time first. Reload the day and let them pick again; their details stay.
        const aboutPreference = typeof err.details === 'object' && err.details !== null && 'provider_preference' in err.details;
        setNotice(aboutPreference ? t('booking.preference_taken') : err.message || t('booking.time_just_taken'));
        setReloadToken((n) => n + 1);
        setSlot(null);
        go('time');
      } else if (err instanceof ApiRequestError && err.status === 409) {
        // The business can't take this booking online. Say why in the API's words and how to reach them.
        setRefusal(err.message || t('booking.cannot_complete'));
      } else if (err instanceof ApiRequestError && err.code === 'validation_failed') {
        // The API names the question each complaint is about, so the message
        // goes beside the field the customer got wrong.
        const byQuestion = answerFieldErrors(err.details);
        setAnswerErrors(byQuestion);
        if (Object.keys(byQuestion).length === 0) setError(err.message);
      } else if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError(t('booking.connection_error'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(()=>{const q=new URLSearchParams(window.location.search),requested=data.services.find(s=>s.id===q.get('service'));if(requested&&!service){chooseService(requested);const professional=data.providers.find(p=>p.id===q.get('provider'));if(professional)setProviderId(professional.id);}},[data]);

  function reset() {
    setResult(null);
    setService(null);
    setLocationId(null);
    setProviderId(null);
    setSlot(null);
    setLastDate(null);
    setNotice(null);
    setRefusal(null);
    setAnswers({});
    setAnswerErrors({});
    setStep('service');
  }

  if (result && service) {
    return (
      <Confirmation
        slug={slug}
        data={data}
        result={result}
        service={service}
        customerZone={customerZone}
        onBookAnother={reset}
      />
    );
  }

  const providerName = providerId ? providers.find((p) => p.id === providerId)?.name ?? null : null;
  const money = (cents: number) => formatMoney(cents, service?.currency || data.organization.currency, locale);
  // In dual pricing the summary shows both, so the customer never sees
  // one number here and a different one at the payment step.
  const feePercent = (bps: number) => String(bps / 100);
  const servicePrice = service && page.show_prices && service.price_cents > 0
    ? (typeof service.cash_price_cents === 'number' && service.cash_price_cents < service.price_cents
        ? `${t('booking.cash_price')} ${money(service.cash_price_cents)} · ${t('booking.card_price')} ${money(service.price_cents)}`
        // A card fee (0048): the price, and what a credit card adds to it.
        : typeof service.card_price_cents === 'number' && service.card_price_cents > service.price_cents
          ? `${money(service.price_cents)} · ${t('booking.card_fee_short', { amount: money(service.card_price_cents), percent: feePercent(page.payment_pricing?.card_fee_bps ?? 0) })}`
          : money(service.price_cents))
    : null;

  return (
    <section id="booking" className="booking card" aria-labelledby="booking-h">
      <h2 id="booking-h">{t('booking.heading')}</h2>
      <StepIndicator steps={steps} current={step} onJump={(id) => go(id as StepId)} />
      <p className="sr-only" aria-live="polite">{announcement}</p>

      {step === 'service' && (
        <div className="step-body">
          <h3>{t('booking.choose_service')}</h3>
          {data.services.length === 0 && <p className="empty">{t('booking.no_services_now')}</p>}
          <ul className="option-list">
            {data.services.map((s) => (
              <li key={s.id}>
                <button type="button" className={`option${service?.id === s.id ? ' is-selected' : ''}`} onClick={() => chooseService(s)} aria-pressed={service?.id === s.id}>
                  <span className="option-main">
                    <span className="option-title">{s.name}</span>
                    {s.description && <span className="option-desc">{s.description}</span>}
                  </span>
                  <span className="option-meta">
                    <span>{formatDuration(s.duration_minutes, locale)}</span>
                    {page.show_prices && s.price_cents > 0 && (
                      // Dual pricing (0045): the cash price and the card
                      // price together, from the very first screen.
                      typeof s.cash_price_cents === 'number' && s.cash_price_cents < s.price_cents ? (
                        <span className="price price--pair">
                          <span className="price-line">{t('booking.cash_price')}: {formatMoney(s.cash_price_cents, s.currency || data.organization.currency, locale)}</span>
                          <span className="price-line">{t('booking.card_price')}: {formatMoney(s.price_cents, s.currency || data.organization.currency, locale)}</span>
                        </span>
                      ) : typeof s.card_price_cents === 'number' && s.card_price_cents > s.price_cents ? (
                        // A card fee (0048): the price, and the credit
                        // card total with the fee disclosed, together.
                        <span className="price price--pair">
                          <span className="price-line">{formatMoney(s.price_cents, s.currency || data.organization.currency, locale)}</span>
                          <span className="price-line small">{t('booking.card_fee_short', { amount: formatMoney(s.card_price_cents, s.currency || data.organization.currency, locale), percent: feePercent(page.payment_pricing?.card_fee_bps ?? 0) })}</span>
                        </span>
                      ) : (
                        <span className="price">{formatMoney(s.price_cents, s.currency || data.organization.currency, locale)}</span>
                      )
                    )}
                    {page.show_prices && (s.deposit_cents ?? 0) > 0 && (
                      <span className="deposit-note">{t('booking.deposit_note', { amount: formatMoney(s.deposit_cents!, s.currency || data.organization.currency, locale) })}</span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {step === 'location' && service && (
        <div className="step-body">
          <h3>{t('booking.choose_location_heading')}</h3>
          <ul className="option-list">
            {locations.map((l) => (
              <li key={l.id}>
                <button type="button" className={`option${locationId === l.id ? ' is-selected' : ''}`} onClick={() => chooseLocation(l.id)} aria-pressed={locationId === l.id}>
                  <span className="option-main">
                    <span className="option-title">{l.name}</span>
                    {l.address && <span className="option-desc">{l.address}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {step === 'preference' && service && preferenceOptions && (
        <div className="step-body">
          <h3>{t('booking.preference_heading')}</h3>
          {page.allow_provider_choice && providers.length > 1 && <p className="muted">{t('booking.preference_hint')}</p>}
          <ul className="option-list">
            {preferenceOptions.map((o) => (
              <li key={o}>
                <button type="button" className={`option${preference === o ? ' is-selected' : ''}`} onClick={() => choosePreference(o)} aria-pressed={preference === o}>
                  <span className="option-main">
                    <span className="option-title">{preferenceLabel(t, o)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {step === 'provider' && service && (
        <div className="step-body">
          <h3>{t('booking.choose_professional')}</h3>
          <ul className="option-list">
            <li>
              <button type="button" className={`option${anyProvider ? ' is-selected' : ''}`} onClick={() => chooseProvider(null)} aria-pressed={anyProvider}>
                <span className="option-main">
                  <span className="option-title">{t('booking.any_available')}</span>
                  <span className="option-desc">{t('booking.any_available_desc')}</span>
                </span>
              </button>
            </li>
            {providers.map((p) => (
              <li key={p.id}>
                <button type="button" className={`option${providerId === p.id ? ' is-selected' : ''}`} onClick={() => chooseProvider(p.id)} aria-pressed={providerId === p.id}>
                  {p.photo_url ? <img className="avatar" src={p.photo_url} alt="" loading="lazy" width={48} height={48} /> : <span className="avatar avatar--initial" aria-hidden="true">{p.name.charAt(0)}</span>}
                  <span className="option-main">
                    <span className="option-title">{p.name}</span>
                    {p.bio && <span className="option-desc">{p.bio}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {step === 'time' && service && (
        <div className="step-body">
          <h3>{t('booking.pick_date_time')}</h3>
          <p className="muted">
            {service.name} · {formatDuration(service.duration_minutes, locale)}
            {providerName ? ` · ${t('booking.with_provider', { name: providerName })}` : narrowed && preference ? ` · ${t('booking.preference_inline', { preference: preferenceLabel(t, preference) })}` : needsProvider ? ` · ${t('booking.any_available_provider_inline')}` : ''}
            {needsLocation && location ? ` · ${location.name}` : ''}
          </p>
          {notice && (
            <p className="notice" role="alert">
              {notice}{' '}
              <button type="button" className="link-btn" onClick={() => setNotice(null)} aria-label={t('booking.dismiss')}>{t('booking.dismiss')}</button>
            </p>
          )}
          {isClass ? (
            <ClassPicker
              slug={slug}
              sessions={sessions ?? []}
              serviceName={service.name}
              details={details}
              onBooked={(r) => { setResult(r); setSessions(null); }}
              onStale={() => setClassToken((n) => n + 1)}
            />
          ) : waitlistDate ? (
            <WaitlistForm
              slug={slug}
              serviceId={service.id}
              serviceName={service.name}
              providerId={providerId}
              locationId={needsLocation ? location?.id ?? null : null}
              providerName={providerName ?? null}
              date={waitlistDate}
              maxAdvanceDays={page.max_advance_days}
              details={details}
              onClose={() => setWaitlistDate(null)}
            />
          ) : (
            <>
              <DateTimePicker
                slug={slug}
                serviceId={service.id}
                providerId={providerId}
                providerPreference={asksPreference ? preference : null}
                onClearPreference={narrowed ? clearPreference : null}
                locationId={needsLocation ? location?.id ?? null : null}
                locationZone={locationZone}
                maxAdvanceDays={page.max_advance_days}
                selected={slot}
                onSelect={chooseSlot}
                reloadToken={reloadToken}
                initialDate={lastDate}
                onAskWaitlist={page.waitlist_enabled ? setWaitlistDate : null}
              />
              <div className="actions">
                <button type="button" className="btn btn-primary" disabled={!slot} onClick={() => go('details')}>
                  Continue
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {step === 'details' && service && slot && (
        <form className="step-body" onSubmit={submit} noValidate>
          <h3>{t('booking.step_details')}</h3>
          <div className="review">
            <AppointmentSummary
              startsAt={slot.starts_at}
              endsAt={slot.ends_at}
              locationZone={locationZone}
              customerZone={customerZone}
              serviceName={service.name}
              durationMinutes={service.duration_minutes}
              providerName={providerName ?? (needsProvider ? t('booking.any_available_provider') : providers[0]?.name)}
              locationName={location?.name}
              address={location?.address ?? page.address}
              price={servicePrice}
            />
            {hasPaymentInfo(page) && (
              <div className="review-pay">
                <span className="review-pay-label">{t('policies.how_to_pay')}</span>
                <PaymentMethods page={page} />
              </div>
            )}
            <button type="button" className="link-btn" onClick={() => go('time')}>{t('booking.change_time')}</button>
          </div>

          <div className="field">
            <label htmlFor="c-name">{t('booking.full_name')}</label>
            <input id="c-name" name="name" type="text" autoComplete="name" required value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="c-email">{t('booking.email')}</label>
            <input id="c-email" name="email" type="email" autoComplete="email" inputMode="email" required value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} />
            <p className="hint">{t('booking.email_hint')}</p>
          </div>
          <div className="field">
            <label htmlFor="c-phone">{t('booking.phone_label')} <span className="muted">{t('booking.optional_marker')}</span></label>
            <input id="c-phone" name="phone" type="tel" autoComplete="tel" inputMode="tel" value={details.phone} onChange={(e) => setDetails({ ...details, phone: e.target.value })} />
            {details.phone.trim() !== '' && (
              <SmsConsent checked={details.sms_opt_in === true} onChange={(checked) => setDetails({ ...details, sms_opt_in: checked })} />
            )}
          </div>
          <div className="field">
            <label htmlFor="c-notes">{t('booking.notes')} <span className="muted">{t('booking.optional_marker')}</span></label>
            <textarea id="c-notes" name="notes" rows={3} maxLength={1000} value={details.notes} onChange={(e) => setDetails({ ...details, notes: e.target.value })} />
          </div>
          {questions.map((q) => (
            <QuestionField
              key={q.id}
              question={q}
              value={answers[q.id] ?? ''}
              error={answerErrors[q.id]}
              onChange={(value) => setAnswers({ ...answers, [q.id]: value })}
            />
          ))}

          <FinancialDisclosure slug={slug} query={new URLSearchParams({service_id:service.id,provider_id:slot.provider_id,starts_at:slot.starts_at}).toString()} onChange={setFinancial}/>
          {refusal && (
            <div className="refusal" role="alert">
              <p className="error">{refusal}</p>
              {hasContact(page) && <p className="small muted">{t('booking.get_in_touch')}</p>}
              <ContactList page={page} />
            </div>
          )}
          {error && <p className="error" role="alert">{error}</p>}
          <div className="actions">
            <button type="button" className="btn btn-secondary" onClick={() => go('time')} disabled={submitting}>{t('booking.back')}</button>
            <button type="submit" className="btn btn-primary" disabled={submitting || !financial.ready || !financial.accepted || !details.name.trim() || !isEmail(details.email)}>
              {submitting ? t('booking.booking_in_progress') : financial.quote?.requires_checkout ? t('booking.continue_checkout') : t('booking.confirm')}
            </button>
          </div>
          <p className="hint">{t('booking.times_in_your_zone', { zone: customerZone.replace(/_/g, ' ') })}</p>
        </form>
      )}
    </section>
  );
}

function isEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

/** `{ questionId: message }` from a validation_failed error's `answers.<id>` paths. */
function answerFieldErrors(details: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!Array.isArray(details)) return out;
  for (const d of details) {
    if (typeof d !== 'object' || d === null) continue;
    const { path, message } = d as { path?: unknown; message?: unknown };
    if (typeof path !== 'string' || typeof message !== 'string') continue;
    const id = path.startsWith('answers.') ? path.slice('answers.'.length) : null;
    if (id && !(id in out)) out[id] = message;
  }
  return out;
}

/** One of the business's own questions, in the same markup as the fields above it. */
function QuestionField({ question, value, error, onChange }: {
  question: PublicQuestion; value: string; error: string | undefined; onChange: (value: string) => void;
}) {
  const id = `q-${question.id}`;
  const options = question.kind === 'choice' ? question.choices : BOOLEAN_CHOICES;
  return (
    <div className="field">
      <label htmlFor={id}>
        {question.label}
        {!question.required && <span className="muted"> (optional)</span>}
      </label>
      {question.kind === 'textarea' ? (
        <textarea id={id} name={id} rows={3} maxLength={2000} required={question.required} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : question.kind === 'text' ? (
        <input id={id} name={id} type="text" maxLength={2000} required={question.required} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <select id={id} name={id} required={question.required} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Please choose…</option>
          {options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      )}
      {question.help_text && <p className="hint">{question.help_text}</p>}
      {error && <p className="error" role="alert">{error}</p>}
    </div>
  );
}

interface ConfirmationProps {
  slug: string;
  data: ActivePageResponse;
  result: AppointmentResponse;
  service: PublicService;
  customerZone: string;
  onBookAnother: () => void;
}

function Confirmation({ slug, data, result, service, customerZone, onBookAnother }: ConfirmationProps) {
  const { t, locale } = useI18n();
  const a = result.appointment;
  const location = data.locations.find((l) => l.id === a.location_id) ?? null;
  const locationZone = a.timezone || location?.timezone || data.organization.timezone;
  const businessName = data.page.display_name || data.organization.name;
  const managePath = `/${encodeURIComponent(slug)}/appointments/${a.id}${a.manage_token ? `?token=${encodeURIComponent(a.manage_token)}` : ''}`;
  const manageUrl = `${window.location.origin}${managePath}`;
  const address = location?.address ?? data.page.address ?? null;
  const priceCents = a.price_cents ?? service.price_cents;
  const price = data.page.show_prices && priceCents > 0 ? formatMoney(priceCents, a.currency || service.currency || data.organization.currency, locale) : null;
  const paid = a.payment_status === 'paid' || a.payment_status === 'waived';
  const depositCents = a.deposit_cents ?? service.deposit_cents ?? 0;
  const deposit = data.page.show_prices && depositCents > 0 ? formatMoney(depositCents, a.currency || service.currency || data.organization.currency, locale) : null;
  const ics = buildIcs({
    uid: `${a.id}@assemble-booking`,
    startsAt: a.starts_at,
    endsAt: a.ends_at,
    summary: `${a.service_name || service.name} at ${businessName}`,
    description: `${a.provider_name ? `With ${a.provider_name}. ` : ''}Manage this appointment: ${manageUrl}`,
    location: [location?.name, address].filter(Boolean).join(', ') || undefined,
    url: manageUrl,
  });

  return (
    <section id="booking" className="booking card confirmation" aria-labelledby="confirm-h">
      <div className="confirm-mark" aria-hidden="true"><CheckIcon /></div>
      <div className="title-row">
        <h2 id="confirm-h">{a.status === 'pending' ? t('booking.complete_your_booking') : t('booking.youre_booked')}</h2>
        <PaidBadge status={a.payment_status} />
      </div>
      <p className="muted">
        {a.status === 'pending' ? t('booking.pending_confirmation') : t('booking.confirmation_on_way')}
      </p>
      <AppointmentSummary
        startsAt={a.starts_at}
        endsAt={a.ends_at}
        locationZone={locationZone}
        customerZone={customerZone}
        serviceName={a.service_name || service.name}
        durationMinutes={service.duration_minutes}
        providerName={a.provider_name}
        locationName={a.location_name || location?.name}
        address={address}
        price={paid ? price : null}
      />
      {!paid && <HowToPay page={data.page} price={price} deposit={deposit} depositPaid={Boolean(a.deposit_paid_at)} />}
      <div className="confirm-actions">
        <a className="btn btn-primary" href={icsDataUrl(ics)} download={`appointment-${a.id.slice(0, 8)}.ics`}>
          <CalendarIcon /> {t('booking.add_to_calendar')}
        </a>
        <Link className="btn btn-secondary" to={managePath}>
          <LinkIcon /> {t('booking.manage_this')}
        </Link>
      </div>
      <p className="hint">
        {t('booking.keep_manage_link', { when: formatDateTime(customerZone, a.starts_at, locale) })}
      </p>
      <p className="confirm-again">
        <button type="button" className="link-btn" onClick={onBookAnother}>{t('booking.book_another')}</button>
      </p>
    </section>
  );
}


/** The customer-facing words for a preference option; an unknown option shows as it is. */
function preferenceLabel(t: (key: 'booking.preference_no_preference' | 'booking.preference_female' | 'booking.preference_male') => string, option: string): string {
  if (option === 'no_preference') return t('booking.preference_no_preference');
  if (option === 'female') return t('booking.preference_female');
  if (option === 'male') return t('booking.preference_male');
  return option;
}
